import { defineRecipe, type ColorPalette } from '@chakra-ui/react';
import { Colors, FontSizes, LineHeights, Radii, Shadows, Space, TextStyles } from '../../types';

// Generic `solid`/`outline`/`ghost`/`link` variants paint from colorPalette.N; the named variants carry the
// brand/gray/error surfaces, and `invert` is applied to the base variants via compoundVariants.

const disabledStyles = {
  base: {
    opacity: 1,
    cursor: 'not-allowed',
    color: Colors.TEXT_DISABLED,
    bg: Colors.SURFACE_DISABLED,
    boxShadow: 'none',
  },
  solid: { bg: Colors.SURFACE_DISABLED, color: Colors.TEXT_DISABLED, borderColor: Colors.TRANSPARENT },
  outline: { bg: Colors.TRANSPARENT, color: Colors.TEXT_DISABLED, borderColor: Colors.SURFACE_DISABLED },
  ghost: { bg: Colors.TRANSPARENT, color: Colors.TEXT_DISABLED, borderColor: Colors.TRANSPARENT },
  invert: {
    bg: 'color-mix(in srgb, var(--udl-colors-surface-tertiary-invert) 50%, transparent)',
    color: 'color-mix(in srgb, var(--udl-colors-text-subdued-invert) 50%, transparent)',
  },
  invertOutline: {
    bg: Colors.TRANSPARENT,
    color: 'color-mix(in srgb, var(--udl-colors-text-subdued-invert) 50%, transparent)',
    borderColor: Colors.CURRENT,
  },
  invertGhost: {
    bg: Colors.TRANSPARENT,
    color: 'color-mix(in srgb, var(--udl-colors-text-subdued-invert) 50%, transparent)',
    borderColor: Colors.TRANSPARENT,
  },
};

const solidFocusStyles = {
  outline: '2px solid',
  outlineColor: Colors.BORDER_FOCUS,
  outlineOffset: '1px',
  boxShadow: 'none',
};

// The invert branch ignores colorPalette entirely, so tertiary+invert shares this exact
// css with ghost+invert.
const ghostInvertStyles = {
  color: Colors.TEXT_EMPHASIS_INVERT,
  bg: Colors.TRANSPARENT,
  borderColor: Colors.TRANSPARENT,
  _hover: {
    color: Colors.TEXT_EMPHASIS_INVERT,
    bg: Colors.SURFACE_TERTIARY_INVERT_HOVER,
    _disabled: { ...disabledStyles.invertGhost },
  },
  _active: { color: Colors.TEXT_EMPHASIS_INVERT, bg: Colors.SURFACE_TERTIARY_INVERT_ACTIVE },
  _disabled: { ...disabledStyles.invertGhost },
};

// `solid`'s light pill for the `gray` and `neutral` palettes, in place of `colorPalette.500`. Recipes take
// no props function, so compoundVariants below select it for those two palettes.
const neutralSurfaceStyles = {
  bg: Colors.SURFACE_TERTIARY,
  color: Colors.TEXT_EMPHASIS,
  borderColor: Colors.TRANSPARENT,
  _hover: { bg: Colors.SURFACE_TERTIARY_HOVER, _disabled: { ...disabledStyles.solid } },
  _active: { bg: Colors.SURFACE_TERTIARY_ACTIVE },
  _focusVisible: { ...solidFocusStyles },
  _disabled: { ...disabledStyles.solid },
};

// Every solid-family variant shares this one invert look. `neutral` is the only semantic
// variant exercised with `invert`, so it's the one wired up as a compoundVariant.
const solidInvertStyles = {
  color: Colors.TEXT_EMPHASIS_INVERT,
  bg: Colors.SURFACE_TERTIARY_INVERT,
  borderColor: Colors.TRANSPARENT,
  _hover: {
    color: Colors.TEXT_EMPHASIS_INVERT,
    bg: Colors.SURFACE_TERTIARY_INVERT_HOVER,
    borderColor: Colors.TRANSPARENT,
    _disabled: { ...disabledStyles.invert },
  },
  _active: {
    color: Colors.TEXT_EMPHASIS_INVERT,
    bg: Colors.SURFACE_TERTIARY_INVERT_ACTIVE,
    borderColor: Colors.TRANSPARENT,
  },
  _focusVisible: { ...solidFocusStyles },
  _disabled: { ...disabledStyles.invert },
};

export const buttonRecipe = defineRecipe({
  base: {
    background: Colors.SURFACE_PAGE,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: Colors.TRANSPARENT,
    borderRadius: Radii.MD,
    display: 'inline-flex',
    gap: '.25em',
    textDecoration: 'none',
    transitionProperty: 'common',
    transitionTimingFunction: 'ease',
    transitionDuration: 'fast',
    '& .chakra-button__icon': {
      m: 0,
      alignSelf: 'center',
      fontSize: '1em',
      minH: '1lh',
      verticalAlign: 'middle',
      display: 'inline-flex',
      alignItems: 'center',
      _empty: { display: 'none' },
    },
    '& svg.fa-icon': {
      margin: '0',
      fontSize: '1em',
      '--fa-fw-width': '1em',
    },
    // Stock `_icon` sizing beats Font Awesome's when layers are disabled.
    '& svg.svg-inline--fa': { width: 'auto', height: '1em' },
    '& svg.svg-inline--fa.fa-fw': { width: 'var(--fa-fw-width, 1.25em)' },
    '& .btn-text': { _empty: { display: 'none' } },
    _hover: { _disabled: { ...disabledStyles.base } },
    _disabled: {
      ...disabledStyles.base,
      _hover: { ...disabledStyles.base },
      '& img': { filter: 'grayscale(100%)', opacity: '0.5' },
    },
  },
  variants: {
    variant: {
      solid: {
        bg: 'colorPalette.500',
        color: Colors.WHITE,
        borderColor: Colors.TRANSPARENT,
        _hover: { bg: 'colorPalette.600', _disabled: { ...disabledStyles.solid } },
        _active: { bg: 'colorPalette.700' },
        _dark: {
          color: 'colorPalette.25',
          bg: 'colorPalette.700',
          _hover: { bg: 'colorPalette.600' },
          _active: { bg: 'colorPalette.500' },
        },
        _focusVisible: { ...solidFocusStyles },
        _disabled: { ...disabledStyles.solid },
      },
      outline: {
        color: Colors.TEXT_EMPHASIS,
        bg: Colors['WHITEALPHA.25'],
        borderColor: Colors.BORDER_DEFAULT,
        _hover: { bg: Colors.SURFACE_HOVER, _disabled: { ...disabledStyles.outline } },
        _active: { bg: Colors.SURFACE_ACTIVE, borderColor: Colors.BORDER_MUTED },
        _disabled: { ...disabledStyles.outline },
      },
      ghost: {
        color: Colors.TEXT_EMPHASIS,
        borderColor: Colors.TRANSPARENT,
        bg: Colors.TRANSPARENT,
        _hover: {
          bg: Colors.SURFACE_TERTIARY_HOVER,
          color: Colors.TEXT_EMPHASIS,
          _disabled: { ...disabledStyles.ghost },
        },
        _active: { bg: Colors.SURFACE_TERTIARY_ACTIVE, color: Colors.TEXT_EMPHASIS, borderColor: Colors.TRANSPARENT },
        _disabled: { ...disabledStyles.ghost },
      },
      link: {
        color: 'colorPalette.600',
        bg: Colors.TRANSPARENT,
        _hover: { textDecoration: 'underline', color: 'colorPalette.700', _disabled: { ...disabledStyles.ghost } },
        _active: { color: 'colorPalette.800' },
        _dark: {
          color: 'colorPalette.300',
          _hover: { color: 'colorPalette.200' },
          _active: { color: 'colorPalette.100' },
        },
        _disabled: { ...disabledStyles.ghost },
      },
      elevated: {
        bg: Colors.SURFACE_DIALOG,
        borderColor: Colors.TRANSPARENT,
        outline: '1px solid',
        outlineColor: Colors.BORDER_ELEVATED,
        color: Colors.TEXT_EMPHASIS,
        boxShadow: Shadows.XS,
        _hover: {
          bg: Colors.SURFACE_DIALOG_HOVER,
          outline: '1px solid',
          outlineColor: Colors.BORDER_ELEVATED,
          boxShadow: Shadows.XS,
          _disabled: { ...disabledStyles.outline },
        },
        _active: { bg: Colors.SURFACE_DIALOG_ACTIVE, boxShadow: Shadows.XS },
        _disabled: { ...disabledStyles.outline },
      },
      // Semantic named variants (brand / gray / error surfaces)
      primary: {
        bg: Colors.SURFACE_PRIMARY,
        color: Colors.TEXT_ON_PRIMARY,
        borderColor: Colors.TRANSPARENT,
        _hover: {
          bg: Colors.SURFACE_PRIMARY_HOVER,
          color: Colors.TEXT_ON_PRIMARY_HOVER,
          _disabled: { ...disabledStyles.solid },
        },
        _active: { bg: Colors.SURFACE_PRIMARY_ACTIVE, color: Colors.TEXT_ON_PRIMARY_ACTIVE },
        _focusVisible: { ...solidFocusStyles },
        _disabled: { ...disabledStyles.solid },
      },
      secondary: {
        color: Colors.TEXT_ON_SECONDARY,
        bg: Colors.SURFACE_SECONDARY,
        borderColor: Colors.BORDER_BRAND,
        _hover: {
          color: Colors.TEXT_ON_SECONDARY_HOVER,
          bg: Colors.SURFACE_SECONDARY_HOVER,
          _disabled: { ...disabledStyles.outline },
        },
        _active: {
          color: Colors.TEXT_ON_SECONDARY_ACTIVE,
          bg: Colors.SURFACE_SECONDARY_ACTIVE,
          borderColor: Colors.SURFACE_BRAND,
        },
        _disabled: { ...disabledStyles.outline },
      },
      tertiary: {
        color: Colors.TEXT_INTERACTIVE,
        bg: Colors.TRANSPARENT,
        _hover: {
          color: Colors.TEXT_INTERACTIVE_HOVER,
          bg: Colors.SURFACE_TERTIARY,
          _disabled: { ...disabledStyles.ghost },
        },
        _active: { color: Colors.TEXT_INTERACTIVE_ACTIVE, bg: Colors.SURFACE_TERTIARY_HOVER },
        _disabled: { ...disabledStyles.ghost },
      },
      neutral: { ...neutralSurfaceStyles },
      destructive: {
        bg: 'error.500',
        color: Colors.WHITE,
        borderColor: Colors.TRANSPARENT,
        _hover: { bg: 'error.600', _disabled: { ...disabledStyles.solid } },
        _active: { bg: 'error.700' },
        _focusVisible: { ...solidFocusStyles },
        _disabled: { ...disabledStyles.solid },
      },
      // `size` resolves after `variant`, so the unstyled reset is restated in compoundVariants.
      unstyled: { bg: 'none', color: 'inherit', display: 'inline', lineHeight: 'inherit', m: 0, p: 0 },
    },
    invert: {
      true: {},
      false: {},
    },
    size: {
      '2xs': {
        borderRadius: Radii.SM,
        px: Space.XS,
        py: Space['2XS'],
        gap: Space['2XS'],
        h: Space.SPACE_6,
        minW: Space.SPACE_6,
        fontSize: FontSizes.SM,
        lineHeight: LineHeights.LINE_HEIGHTS_5,
      },
      xs: {
        borderRadius: Radii.BASE,
        px: Space.SM,
        py: Space.XS,
        gap: Space['2XS'],
        h: Space.SPACE_7,
        minW: Space.SPACE_7,
        fontSize: FontSizes.SM,
        lineHeight: LineHeights.LINE_HEIGHTS_5,
      },
      sm: {
        borderRadius: Radii.MD,
        px: Space.XS,
        py: Space.XS,
        gap: Space.SPACE_1_5,
        minW: Space.SPACE_8,
        h: Space.SPACE_8,
        textStyle: TextStyles.UI_DEFAULT,
      },
      md: {
        borderRadius: Radii.LG,
        py: Space.SPACE_2_5,
        px: 'calc(var(--udl-spacing-lg) / 2)',
        gap: Space.XS,
        minW: Space.SPACE_10,
        h: 'calc(var(--udl-spacing-md) * 2)',
        fontSize: FontSizes.SM,
        lineHeight: LineHeights.LINE_HEIGHTS_5,
        textStyle: TextStyles.UI_DEFAULT,
      },
      lg: {
        borderRadius: Radii.XL,
        py: Space.SM,
        px: Space.MD,
        gap: Space.XS,
        minW: Space.SPACE_12,
        h: Space.SPACE_12,
        fontSize: FontSizes.MD,
        lineHeight: LineHeights.LINE_HEIGHTS_5,
      },
      compact: {
        borderRadius: Radii.MD,
        px: 'calc(var(--udl-spacing-md) / 2)',
        py: Space.XS,
        gap: Space.SPACE_1_5,
        minW: Space.SPACE_8,
        h: Space.SPACE_8,
        textStyle: TextStyles.UI_COMPACT,
      },
    },
  },
  compoundVariants: [
    // Must precede the solid+invert entry so an explicit `invert` still wins. Chakra's `ColorPalette` type
    // lists stock hues only and can't be augmented, so a custom family like `neutral` needs this cast.
    {
      variant: 'solid',
      colorPalette: ['gray', 'neutral' as ColorPalette],
      css: neutralSurfaceStyles,
    },
    {
      variant: 'solid',
      invert: true,
      css: solidInvertStyles,
    },
    {
      variant: 'neutral',
      invert: true,
      css: solidInvertStyles,
    },
    {
      variant: 'outline',
      invert: true,
      css: {
        color: Colors.TEXT_EMPHASIS_INVERT,
        bg: Colors.TRANSPARENT,
        borderColor: Colors.BORDER_INVERT,
        _hover: { bg: Colors.SURFACE_TERTIARY_INVERT_HOVER, _disabled: { ...disabledStyles.invertOutline } },
        _active: { bg: Colors.SURFACE_TERTIARY_INVERT_ACTIVE },
        _disabled: { ...disabledStyles.invertOutline },
      },
    },
    {
      variant: 'ghost',
      colorPalette: 'brand' as ColorPalette,
      css: {
        color: Colors.TEXT_INTERACTIVE,
        _hover: { color: Colors.TEXT_INTERACTIVE_HOVER },
        _active: { color: Colors.TEXT_INTERACTIVE_ACTIVE },
      },
    },
    {
      variant: 'ghost',
      invert: true,
      css: ghostInvertStyles,
    },
    {
      variant: 'tertiary',
      invert: true,
      css: ghostInvertStyles,
    },
    // cva applies `size` after `variant`, so only a compound entry can reset the size box for `link`.
    {
      variant: 'link',
      css: {
        p: 0,
        h: 'auto',
        _hover: { _disabled: { textDecoration: 'none' } },
      },
    },
    {
      variant: 'unstyled',
      css: { p: 0 },
    },
  ],
  defaultVariants: {
    variant: 'outline',
    size: 'md',
    colorPalette: 'gray' as ColorPalette,
  },
});
